import { ElementType, ReactNode } from 'react';
import { cn } from '../../utils';
import { CheckIcon } from '@heroicons/react/20/solid';

type StepsProps = {
    steps: Step[];
    LinkAs?: ElementType;
    className?: string | object;
};

type Step = {
    id: string;
    name: string;
    status: STATUS;
    href?: string;
};

export enum STATUS {
    COMPLETE = 'complete',
    CURRENT = 'current',
    UPCOMING = 'upcoming',
    DISABLED = 'disabled',
}

export default function Steps({
    steps,
    className,
    LinkAs = LinkHandler,
    ...rest
}: StepsProps) {
    return (
        <nav
            className={cn('alli-flex alli-w-full alli-flex-grow', className)}
            {...rest}>
            <ol
                role="list"
                className="alli-flex alli-w-full alli-items-center alli-text-center">
                {steps.map((step, index) => {
                    return (
                        <Status
                            LinkAs={LinkAs}
                            key={index}
                            step={step}
                            last={steps.length - 1 === index}
                            first={0 === index}
                            prevStatus={
                                index === 0 ? null : steps[index - 1].status
                            }
                        />
                    );
                })}
            </ol>
        </nav>
    );
}

type StatusProps = {
    LinkAs: ElementType;
    step: Step;
    prevStatus: STATUS | null;
    last: boolean;
    first: boolean;
};

function Status({ step, last, first, prevStatus, LinkAs }: StatusProps) {
    return (
        <li
            key={step.name}
            className="alli-relative alli-w-full alli-flex-1 alli-justify-center alli-text-center">
            <div className="alli-h-full alli-w-full">
                <BackgroundBar
                    first={first}
                    last={last}
                    prevStatus={prevStatus}
                    currStatus={step.status}
                />
                <LinkAs
                    step={step}
                    className={cn({
                        'alli-relative alli-mx-auto alli-flex alli-h-8 alli-w-8 alli-items-center alli-justify-center alli-rounded-full alli-bg-blue-600 hover:alli-bg-blue-900':
                            step.status === STATUS.COMPLETE,
                        'alli-relative alli-mx-auto alli-flex alli-h-8 alli-w-8 alli-items-center alli-justify-center alli-rounded-full alli-border-2 alli-border-blue-600 alli-bg-white':
                            step.status === STATUS.CURRENT,
                        'alli-group alli-relative alli-mx-auto alli-flex alli-h-8 alli-w-8 alli-items-center alli-justify-center alli-rounded-full alli-border-2 alli-border-gray-700 alli-bg-white hover:alli-border-gray-700':
                            step.status === STATUS.UPCOMING,
                        'alli-group alli-relative alli-mx-auto alli-flex alli-h-8 alli-w-8 alli-cursor-not-allowed alli-items-center alli-justify-center alli-rounded-full alli-border-2 alli-border-gray-700 alli-bg-white hover:alli-border-gray-700':
                            step.status === STATUS.DISABLED,
                    })}>
                    <StatusIcon status={step.status} />
                </LinkAs>
                <div
                    data-testid={`${step.id}|${step.status}`}
                    className={cn('alli-mt-2 alli-text-nowrap', {
                        'alli-text-gray-600': step.status === STATUS.DISABLED,
                    })}>
                    {step.name}
                </div>
            </div>
        </li>
    );
}

export type StepLinkAsProps = {
    step: Step;
    className: string;
    children: ReactNode;
};

function LinkHandler({ step, className, children }: StepLinkAsProps) {
    return (
        <a href={step.href} className={className}>
            {children}
        </a>
    );
}

function BackgroundBar({
    first,
    last,
    currStatus,
    prevStatus,
}: {
    first: boolean;
    last: boolean;
    currStatus: STATUS | null;
    prevStatus: STATUS | null;
}) {
    return (
        <div className="alli-absolute alli-inset-0 alli-top-1/4 alli-flex alli-h-full alli-justify-center">
            <HalfBackground status={prevStatus} transparent={first} />
            <HalfBackground status={currStatus} transparent={last} />
        </div>
    );
}

function HalfBackground({
    status,
    transparent,
}: {
    status: STATUS | null;
    transparent: boolean;
}) {
    return (
        <div
            className={cn(
                {
                    'alli-bg-blue-600': status === STATUS.COMPLETE,
                    'alli-bg-gray-700':
                        status === STATUS.CURRENT ||
                        status === STATUS.UPCOMING ||
                        status === STATUS.DISABLED,
                    'alli-bg-transparent': transparent,
                },
                'alli-h-0.5 alli-w-full',
            )}
        />
    );
}

function StatusIcon({ status }: { status: STATUS | null }) {
    switch (status) {
        case STATUS.COMPLETE:
            return (
                <CheckIcon
                    data-testid="statusiconcomplete"
                    className="alli-h-5 alli-w-5 alli-text-white"
                    aria-hidden="true"
                />
            );

        case STATUS.CURRENT:
            return (
                <span
                    data-testid="statusiconcurrent"
                    className="alli-h-2.5 alli-w-2.5 alli-rounded-full alli-bg-blue-600"
                    aria-hidden="true"
                />
            );

        case STATUS.UPCOMING:
            return (
                <span
                    data-testid="statusiconupcoming"
                    className="alli-h-2.5 alli-w-2.5 alli-rounded-full alli-bg-transparent group-hover:alli-bg-gray-600"
                    aria-hidden="true"
                />
            );

        default:
            return null;
    }
}
